import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { isValidContactFormVariant } from "../../types/contactFormVariants.ts";
import { ContactFormModal, type ContactFormModalHandle } from "../components/modals/ContactFormModal.tsx";
import { ContactFormModalv2, type ContactFormModalv2Handle } from "../components/modals/ContactFormModalv2.tsx";
import { ContactFormModalv3, type ContactFormModalv3Handle } from "../components/modals/ContactFormModalv3.tsx";
import { EidModal, type EidModalHandle } from "../components/modals/EidModal.tsx";
import { LoginModal, type LoginModalHandle } from "../components/modals/LoginModal.tsx";
import { ReserveCarInfoModal, type ReserveCarInfoModalHandle } from "../components/modals/ReserveCarInfoModal.tsx";
import { ReserveCarModal, type ReserveCarModalHandle } from "../components/modals/ReserveCarModal.tsx";
import { useTracking } from "../utils/pulseHelper.ts";
import { useSafeDealerItemContext, useValidDealerItemContext } from "./DealerItemProviderContext.tsx";

type ModalContextType = {
  openLoginModal: () => void;
  openContactFlow: () => void;
  openReserveCarModal: () => void;
  openReserveCarInfoModal: () => void;
  isReserved: boolean;
  setIsReserved: Dispatch<SetStateAction<boolean>>;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

const noopModal = () => {};

/**
 * Provides modal open functions without rendering actual modals.
 * Useful for tests that don't need modal behavior.
 */
export function MockModalProvider({ children }: { children: ReactNode }) {
  const [isReserved, setIsReserved] = useState(false);
  return (
    <ModalContext
      value={{
        openLoginModal: noopModal,
        openContactFlow: noopModal,
        openReserveCarModal: noopModal,
        openReserveCarInfoModal: noopModal,
        isReserved,
        setIsReserved,
      }}
    >
      {children}
    </ModalContext>
  );
}

/**
 * ModalProvider that safely checks for dealer data availability.
 * If dealer data is not available, provides noop modal functions.
 */
export function ModalProvider({ children }: { children: ReactNode }) {
  const dealerItemData = useSafeDealerItemContext();

  if (!dealerItemData) {
    return <MockModalProvider>{children}</MockModalProvider>;
  }

  return <ModalProviderInner>{children}</ModalProviderInner>;
}

/**
 * Inner provider that renders modals when dealer data is guaranteed to be available.
 */
function ModalProviderInner({ children }: { children: ReactNode }) {
  const loginModalRef = useRef<LoginModalHandle>(null);
  const eidModalRef = useRef<EidModalHandle>(null);
  const contactFormV1Ref = useRef<ContactFormModalHandle>(null);
  const contactFormV2Ref = useRef<ContactFormModalv2Handle>(null);
  const contactFormV3Ref = useRef<ContactFormModalv3Handle>(null);
  const reserveCarModalRef = useRef<ReserveCarModalHandle>(null);
  const reserveCarInfoModalRef = useRef<ReserveCarInfoModalHandle>(null);
  const [isReserved, setIsReserved] = useState(false);

  const {
    external: {
      isLoggedIn,
      newContactFormV2,
      newContactFormV3,
      eidAuth,
      reservationEnabled,
      isItemReserved,
      openContactForm,
      isPreview,
    },
  } = useValidDealerItemContext();

  const { trackViewEIDModal } = useTracking();

  const openLoginModal = () => {
    loginModalRef.current?.open();
  };

  /**
   * Opens the appropriate modal in the contact-dealer flow:
   * 1. eID verification modal if required
   * 2. The correct contact form variant
   */
  const openContactFlow = useCallback(() => {
    if (eidAuth.userMustVerify) {
      trackViewEIDModal();
      eidModalRef.current?.open("sendMessage");
      return;
    }

    // Open the appropriate contact form variant
    if (newContactFormV3 && isValidContactFormVariant(newContactFormV3)) {
      if (newContactFormV3 === "v3") {
        contactFormV3Ref.current?.open();
      } else if (newContactFormV3 === "v2") {
        contactFormV2Ref.current?.open();
      } else {
        contactFormV1Ref.current?.open();
      }
    } else if (newContactFormV2) {
      contactFormV2Ref.current?.open();
    } else {
      contactFormV1Ref.current?.open();
    }
  }, [eidAuth, newContactFormV2, newContactFormV3, trackViewEIDModal]);

  // Auto-open contact form when ?contact query parameter is present.
  useEffect(() => {
    if (!isPreview && openContactForm) {
      const url = new URL(window.location.href);
      url.searchParams.delete("contact");
      window.history.replaceState({}, document.title, url.toString());
      openContactFlow();
    }
  }, [openContactForm, isPreview, openContactFlow]);

  const openReserveCarModal = () => {
    reserveCarModalRef.current?.open();
  };

  const openReserveCarInfoModal = () => {
    reserveCarInfoModalRef.current?.open();
  };

  const resolveContactFormModal = () => {
    if (newContactFormV3 && isValidContactFormVariant(newContactFormV3)) {
      const modals: Record<string, ReactNode> = {
        v1: <ContactFormModal ref={contactFormV1Ref} />,
        v2: <ContactFormModalv2 ref={contactFormV2Ref} />,
        v3: <ContactFormModalv3 ref={contactFormV3Ref} />,
      };
      return modals[newContactFormV3];
    }
    return newContactFormV2 ? (
      <ContactFormModalv2 ref={contactFormV2Ref} />
    ) : (
      <ContactFormModal ref={contactFormV1Ref} />
    );
  };

  return (
    <ModalContext
      value={{
        openLoginModal,
        openContactFlow,
        openReserveCarModal,
        openReserveCarInfoModal,
        isReserved: isReserved || isItemReserved,
        setIsReserved,
      }}
    >
      {isLoggedIn && eidAuth.userMustVerify && <EidModal ref={eidModalRef} />}
      {isLoggedIn && !eidAuth.userMustVerify && resolveContactFormModal()}

      {!isLoggedIn && reservationEnabled && !isItemReserved && <LoginModal ref={loginModalRef} />}
      {reservationEnabled && !isItemReserved && (
        <ReserveCarModal ref={reserveCarModalRef} setIsReserved={setIsReserved} />
      )}
      <ReserveCarInfoModal ref={reserveCarInfoModalRef} />
      {children}
    </ModalContext>
  );
}

export function useModals() {
  const context = useContext(ModalContext);
  if (typeof context === "undefined") {
    throw new Error("useModals must be used within a ModalProvider");
  }
  return context;
}
